'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Icon from '@/components/ui/AppIcon';
import { SERVICES } from '@/lib/services';
import { LOCATIONS } from '@/lib/locations';
import { INDUSTRIES } from '@/lib/industries';

const quickLinks = [
  { label: 'Services', href: '/services' },
  { label: 'Industries', href: '/industries' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Free Tools', href: '/tools' },
  { label: 'Service Areas', href: '/locations' },
  { label: 'Results', href: '/#our-clients' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: '/contact' },
];

// Every service page linked from every page on the site. A page reachable
// only from the sitemap accumulates no internal link equity, which is the
// state these pages would otherwise be in.
const serviceLinks = SERVICES.map((service) => ({
  label: service.navLabel,
  href: `/services/${service.slug}`,
}));

const industryLinks = INDUSTRIES.map((industry) => ({
  label: industry.navLabel,
  href: `/industries/${industry.slug}`,
}));

const locationLinks = LOCATIONS.map((location) => ({
  label: location.city,
  href: `/locations/${location.slug}`,
}));

// Surfaced in the footer so every page links into the guides, giving the blog
// posts an internal link from site-wide navigation.
const guideLinks = [
  { label: 'High-Converting Web Design', href: '/blog/high-converting-website-design' },
  { label: 'Google Ads Playbook', href: '/blog/google-ads-performance-marketing-guide' },
  { label: 'SEO for Service Businesses', href: '/blog/seo-for-local-service-businesses' },
  { label: 'Meta Ads Strategy', href: '/blog/meta-ads-strategy-for-small-business' },
  { label: 'Generative Engine Optimization', href: '/blog/generative-engine-optimization-guide' },
  { label: 'AI Search for Home Services', href: '/blog/ai-search-for-home-service-businesses' },
  { label: 'Cost Per Lead Calculator', href: '/tools/cost-per-lead-calculator' },
  { label: 'All Guides', href: '/blog' },
];

export default function Footer() {
  return (
    <footer className="bg-background border-t border-border-light">
      <div className="max-w-content mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
        {/* Main row */}
        <div className="flex flex-col md:flex-row justify-between gap-16 pb-16 border-b border-border-light">
          {/* Brand */}
          <div className="flex flex-col gap-4 max-w-xs">
            <div className="flex items-center gap-2">
              <Image
                src="/assets/images/wbglologo-1780491308791.png"
                alt="WeBuildPro Global logo"
                width={36}
                height={36}
                style={{ height: '36px', width: '36px' }}
                loading="lazy"
              />
              <span className="font-sans font-medium text-[15px] tracking-[0.02em] text-foreground">
                WeBuildPro <span className="text-gradient">Global</span>
              </span>
            </div>
            <p className="text-muted text-[15px] leading-relaxed">
              We build the website that converts — and run the ads that fill it. One team. One growth system.
            </p>
            <div className="flex flex-col gap-2 mt-2">
              <a
                href="tel:+16462671282"
                className="text-sm text-muted hover:text-accent transition-colors duration-300 flex items-center gap-2.5"
              >
                <Icon name="PhoneIcon" size={14} className="text-accent shrink-0" />
                +1 (646) 267-1282
              </a>
              <a
                href="mailto:trywebuildpro@gmail.com"
                className="text-sm text-muted hover:text-accent transition-colors duration-300 flex items-center gap-2.5"
              >
                <Icon name="EnvelopeIcon" size={14} className="text-accent shrink-0" />
                trywebuildpro@gmail.com
              </a>
              <span className="text-sm text-muted flex items-center gap-2.5">
                <Icon name="MapPinIcon" size={14} className="text-accent shrink-0" />
Worldwide
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col gap-3">
            <span className="text-eyebrow font-semibold uppercase text-muted-foreground mb-2">Quick Links</span>
            <div className="flex flex-col gap-2">
              {quickLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-sm text-muted hover:text-foreground transition-colors duration-300 text-left"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="flex flex-col gap-3">
            <span className="text-eyebrow font-semibold uppercase text-muted-foreground mb-2">Services</span>
            <div className="flex flex-col gap-2">
              {serviceLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm text-muted hover:text-foreground transition-colors duration-300 text-left"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Industries */}
          <div className="flex flex-col gap-3">
            <span className="text-eyebrow font-semibold uppercase text-muted-foreground mb-2">Industries</span>
            <div className="flex flex-col gap-2">
              {industryLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm text-muted hover:text-foreground transition-colors duration-300 text-left"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Guides */}
          <div className="flex flex-col gap-3">
            <span className="text-eyebrow font-semibold uppercase text-muted-foreground mb-2">Guides</span>
            <div className="flex flex-col gap-2">
              {guideLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm text-muted hover:text-foreground transition-colors duration-300 text-left"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Connect */}
          <div className="flex flex-col gap-3">
            <span className="text-eyebrow font-semibold uppercase text-muted-foreground mb-2">Connect</span>
            <div className="flex flex-col gap-2">
              <a
                href="mailto:trywebuildpro@gmail.com"
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-border-light text-muted hover:text-foreground hover:border-border-strong hover:bg-white/5 transition-all duration-300 ease-premium text-sm font-medium"
              >
                <Icon name="EnvelopeIcon" size={16} />
                Email Us
              </a>
              <a
                href="tel:+16462671282"
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-border-light text-muted hover:text-foreground hover:border-border-strong hover:bg-white/5 transition-all duration-300 ease-premium text-sm font-medium"
              >
                <Icon name="PhoneIcon" size={16} />
                +1 (646) 267-1282
              </a>
              <a
                href="https://wa.me/16462671282"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-border-light text-muted hover:text-foreground hover:border-border-strong hover:bg-white/5 transition-all duration-300 ease-premium text-sm font-medium"
              >
                <Icon name="ChatBubbleLeftEllipsisIcon" size={16} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Service areas — a site-wide internal link into each location page */}
        <div className="pt-10 flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="text-eyebrow font-semibold uppercase text-muted-foreground">
            Service Areas
          </span>
          {locationLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-sm text-muted hover:text-foreground transition-colors duration-300"
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="pt-10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-muted-foreground">© 2026 WeBuildPro Global. All rights reserved.</p>
          <div className="flex gap-8 text-xs text-muted-foreground">
            <Link href="/privacy-policy" className="hover:text-foreground transition-colors duration-300">Privacy Policy</Link>
            <Link href="/terms-of-service" className="hover:text-foreground transition-colors duration-300">Terms of Service</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors duration-300">Blog</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}